import { useEffect, useState } from 'react';
import { App as AntApp, Button, InputNumber, Modal, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { ErrorAlert } from '@/components/ErrorAlert';
import { useUpdatePlatformSettingsMutation } from './hooks';

const { Paragraph, Text } = Typography;

const PRESETS = [10_000, 30_000, 50_000];

interface ThresholdModalProps {
  open: boolean;
  currentThreshold: number;
  onClose: () => void;
}

// Быстрый диалог «Настроить порог» блокировки SMS (из карточки баланса, M5.4).
// PATCH /platform/settings { balance_threshold_kzt }. Полный набор порогов/квот —
// на экране «Настройки».
export function ThresholdModal({
  open,
  currentThreshold,
  onClose,
}: ThresholdModalProps): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [value, setValue] = useState<number>(currentThreshold);
  const mutation = useUpdatePlatformSettingsMutation();

  useEffect(() => {
    if (open) {
      setValue(currentThreshold);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentThreshold]);

  const handleSave = (): void => {
    mutation.mutate(
      { balance_threshold_kzt: Math.max(0, Math.round(value)) },
      {
        onSuccess: () => {
          void message.success(t('platform.threshold.saved'));
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      title={t('platform.threshold.title')}
      open={open}
      onCancel={onClose}
      destroyOnHidden
      footer={
        <Space>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="primary"
            loading={mutation.isPending}
            onClick={handleSave}
            data-testid="threshold-save"
          >
            {t('common.save')}
          </Button>
        </Space>
      }
    >
      <Paragraph type="secondary">{t('platform.threshold.description')}</Paragraph>
      <Text
        type="secondary"
        style={{ display: 'block', marginBottom: 8, fontSize: 12, textTransform: 'uppercase' }}
      >
        {t('platform.threshold.label')}
      </Text>
      <InputNumber<number>
        value={value}
        onChange={(v) => setValue(v ?? 0)}
        min={0}
        step={1000}
        style={{ width: '100%' }}
        addonAfter="₸"
        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
        parser={(v) => Number((v ?? '').replace(/\s/g, '')) as unknown as number}
        data-testid="threshold-input"
      />
      <Space wrap style={{ marginTop: 12 }}>
        {PRESETS.map((p) => (
          <Button key={p} size="small" onClick={() => setValue(p)}>
            {p.toLocaleString('ru-RU')} ₸
          </Button>
        ))}
      </Space>
      {mutation.error && (
        <div style={{ marginTop: 12 }}>
          <ErrorAlert error={mutation.error} />
        </div>
      )}
    </Modal>
  );
}
